import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAudioSettingsOptional } from '../context/AudioSettingsContext';
import './GameSettings.css';

const SettingsIcon = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
    <path
      fill="currentColor"
      d="M12 8.25a3.75 3.75 0 1 0 0 7.5 3.75 3.75 0 0 0 0-7.5Zm9.11 4.88a7.97 7.97 0 0 0 .05-.94 7.97 7.97 0 0 0-.05-.94l2.03-1.58a.5.5 0 0 0 .12-.64l-1.92-3.32a.5.5 0 0 0-.6-.22l-2.39.96a7.2 7.2 0 0 0-1.62-.94l-.36-2.54a.5.5 0 0 0-.5-.42h-3.84a.5.5 0 0 0-.5.42l-.36 2.54c-.58.22-1.12.52-1.62.94l-2.39-.96a.5.5 0 0 0-.6.22L2.69 8.03a.5.5 0 0 0 .12.64l2.03 1.58c-.03.31-.05.63-.05.94s.02.63.05.94L2.81 13.7a.5.5 0 0 0-.12.64l1.92 3.32a.5.5 0 0 0 .6.22l2.39-.96c.5.42 1.04.72 1.62.94l.36 2.54a.5.5 0 0 0 .5.42h3.84a.5.5 0 0 0 .5-.42l.36-2.54c.58-.22 1.12-.52 1.62-.94l2.39.96a.5.5 0 0 0 .6-.22l1.92-3.32a.5.5 0 0 0-.12-.64l-2.03-1.58Z"
    />
  </svg>
);

function SettingsToggle({ label, checked, onChange, disabled }) {
  return (
    <label className={`game-settings__row${disabled ? ' game-settings__row--disabled' : ''}`}>
      <span className="game-settings__label">{label}</span>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        className={`game-settings__switch${checked ? ' game-settings__switch--on' : ''}`}
        onClick={() => !disabled && onChange(!checked)}
        disabled={disabled}
        data-silent-click="true"
      >
        <span className="game-settings__switch-thumb" />
      </button>
    </label>
  );
}

const GameSettings = () => {
  const { t } = useTranslation('common');
  const audioCtx = useAudioSettingsOptional();
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  if (!audioCtx) return null;

  const { settings, saveSettings } = audioCtx;

  const handleAllSounds = (value) => {
    saveSettings({ allSoundsEnabled: value });
  };

  const handleMusic = (value) => {
    saveSettings({ musicEnabled: value });
  };

  const handleCharacter = (value) => {
    saveSettings({ characterSoundsEnabled: value });
  };

  return (
    <div className="game-settings" ref={rootRef}>
      <button
        type="button"
        className="game-settings__trigger"
        onClick={() => setOpen((prev) => !prev)}
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-label={t('settingsTitle')}
        data-silent-click="true"
      >
        <SettingsIcon />
      </button>

      {open && (
        <div className="game-settings__popup" role="dialog" aria-label={t('settingsTitle')}>
          <h3 className="game-settings__title">{t('settingsTitle')}</h3>
          <SettingsToggle
            label={t('settingsAllSounds')}
            checked={settings.allSoundsEnabled}
            onChange={handleAllSounds}
          />
          <SettingsToggle
            label={t('settingsMusic')}
            checked={settings.musicEnabled}
            onChange={handleMusic}
            disabled={!settings.allSoundsEnabled}
          />
          <SettingsToggle
            label={t('settingsCharacterSounds')}
            checked={settings.characterSoundsEnabled}
            onChange={handleCharacter}
            disabled={!settings.allSoundsEnabled}
          />
        </div>
      )}
    </div>
  );
};

export default GameSettings;
